/**
 * #269: custom range selection for conversation export/copy.
 *
 * The user marks a start and an end message in the chat; export/copy then
 * slice by message INDEX (not timestamp — adjacent user+assistant messages
 * can share a timestamp, so index is the only lossless client-side cut).
 * Anchors are keyed by message.id with the captured index as fallback:
 * live-streamed messages carry client-generated ids until the Neo4j id
 * arrives, and legacy rows may lack a stable messageId, so an id can
 * disappear from the list while the position remains valid.
 *
 * Pure and dependency-free so slicing and the click-to-mark state machine
 * are unit-testable without rendering the message list.
 */

export interface MessageAnchor {
  /** message.id at the time of the click — authoritative when still present. */
  id: string;
  /** Index in the rendered (chronologically sorted) list — fallback resolution. */
  index: number;
}

export interface MessageRangeSelection {
  start: MessageAnchor | null;
  end: MessageAnchor | null;
}

export const EMPTY_SELECTION: MessageRangeSelection = { start: null, end: null };

/**
 * Resolve an anchor to an index in `items`: by id when present, else by the
 * captured index when still in bounds, else null (anchor no longer valid).
 */
export function resolveAnchorIndex<T extends { id: string }>(
  items: readonly T[],
  anchor: MessageAnchor | null,
): number | null {
  if (!anchor) return null;
  const byId = items.findIndex((item) => item.id === anchor.id);
  if (byId !== -1) return byId;
  if (anchor.index >= 0 && anchor.index < items.length) return anchor.index;
  return null;
}

/**
 * Resolve the selection to an inclusive index range over `items`.
 * Start-only selections resolve to a single-message range; anchors marked
 * out of order are swapped. Returns null when nothing (valid) is selected.
 */
export function getSelectedIndexRange<T extends { id: string }>(
  items: readonly T[],
  selection: MessageRangeSelection,
): { from: number; to: number } | null {
  const start = resolveAnchorIndex(items, selection.start);
  if (start === null) return null;
  const end = resolveAnchorIndex(items, selection.end);
  if (end === null) return { from: start, to: start };
  return { from: Math.min(start, end), to: Math.max(start, end) };
}

/** Inclusive index-based slice of the selected range ([] when none selected). */
export function sliceBySelection<T extends { id: string }>(
  items: readonly T[],
  selection: MessageRangeSelection,
): T[] {
  const range = getSelectedIndexRange(items, selection);
  if (!range) return [];
  return items.slice(range.from, range.to + 1);
}

/** Number of messages currently covered by the selection. */
export function countSelected<T extends { id: string }>(
  items: readonly T[],
  selection: MessageRangeSelection,
): number {
  const range = getSelectedIndexRange(items, selection);
  return range ? range.to - range.from + 1 : 0;
}

/**
 * Click-to-mark state machine:
 *   nothing marked      → clicked message becomes the start
 *   start only, same    → clicking the lone start again clears the selection
 *   start only, other   → clicked message becomes the end (range complete)
 *   start + end marked  → restart: clicked message becomes the new start
 */
export function nextSelection(
  current: MessageRangeSelection,
  clicked: MessageAnchor,
): MessageRangeSelection {
  if (!current.start) return { start: clicked, end: null };
  if (!current.end) {
    if (current.start.id === clicked.id) return EMPTY_SELECTION;
    return { start: current.start, end: clicked };
  }
  return { start: clicked, end: null };
}
